import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { Button, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons';
import { AbsolutelyCentered } from './components/absolutely-centered';
import { ColorPalette } from './components/config/color-palette';
import { TypographySettings } from './components/config/typography-settings';
import { useMetaTitle } from './hooks/use-meta-title';
import { useBroadcastContext } from './hooks/use-broadcast';
import { formatDateTime } from './utilities/date-helper';
import { useNotifier } from './components/notifier';

const useStyles = makeStyles()(() =>
  ({
    wrapper: {
      flexDirection: 'column',
      gap: '1.5rem',
    },
    header: {
      color: ColorPalette.DeepGalaxy,
      fontFamily: TypographySettings.AlternativeFontFamily,
      fontSize: '12rem',
      fontWeight: 300,
      lineHeight: 1,
      margin: 0,
    },
    content: {
      fontFamily: TypographySettings.AlternativeFontFamily,
      fontSize: '1rem',
      margin: 0,
      textAlign: 'center',
      maxWidth: '600px',
    },
    correlationContainer: {
      fontFamily: TypographySettings.AlternativeFontFamily,
      fontSize: '0.875rem',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
  })
);

interface AccessDeniedPageProps {
  onNavigateHome: () => void;
  message: string;
  correlationIdentifier?: string;
  timestamp?: string;
  show: boolean;
}

export function AccessDeniedPage(props: AccessDeniedPageProps) {
  const { onNavigateHome, message, correlationIdentifier, timestamp, show } = props;
  const { classes } = useStyles();
  // We explicitly avoid using notifier here to avoid circular dependencies (modifying state of notifier context)
  const { send: sendInfo } = useBroadcastContext('GlobalToasterInfo');

  useMetaTitle(show ? '403 - Adgang nægtet' : '');

  function handleCopy() {
    const textToCopy = `${formatDateTime(timestamp)} ${correlationIdentifier}`;
    navigator.clipboard.writeText(textToCopy);
    sendInfo('Kopieret til udklipsholder');
  }

  return (
    <span style={{ display: show === true ? '' : 'none' }}>
      <AbsolutelyCentered className={classes.wrapper}>
        <p className={classes.content}>{message}</p>
        {correlationIdentifier && (
          <div className={classes.correlationContainer}>
            <span>
              Brug venligst følgende ID ved supporthenvendelser: <strong>{correlationIdentifier}</strong>
            </span>
            <IconButton onClick={handleCopy} title="Kopiér information til udklipsholder" size="small">
              <FontAwesomeIcon icon={faCopy} />
            </IconButton>
          </div>
        )}
        <Button variant="contained" color="primary" onClick={onNavigateHome} data-testid="go-home-button">
          Gå til forsiden
        </Button>
      </AbsolutelyCentered>
    </span>
  );
}
